import { FlexRow } from '@/components/common/Flex';
import { MotionPath } from '@/components/common/Motion';
import { FlexProps } from '@chakra-ui/react';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import Pie, { PieArcDatum, ProvidedProps } from '@visx/shape/lib/shapes/Pie';
import chroma from 'chroma-js';
import { animate, useMotionValue, useTransform } from 'framer-motion';
import { useEffect } from 'react';

type Datum = {
    key: string;
    value: number;
    color: string;
    label?: string;
};

export interface PieChartProps
    extends Omit<FlexProps, 'onMouseEnter' | 'onClick'> {
    data: Datum[];
    onMouseEnter?: (datum: Datum) => void;
    onMouseLeave?: () => void;
    onClick?: (datum: Datum) => void;
}

export default function PieChart({
    data,
    onMouseEnter,
    onMouseLeave,
    onClick,
    ...props
}: PieChartProps) {
    return (
        <FlexRow {...props}>
            <ParentSize>
                {({ width, height }) => (
                    <PieChartInner
                        data={data}
                        width={width}
                        height={height}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onClick={onClick}
                    />
                )}
            </ParentSize>
        </FlexRow>
    );
}

interface PieChartInnerProps extends PieChartProps {
    width: number;
    height: number;
}

function PieChartInner({
    data,
    width,
    height,
    onMouseEnter,
    onMouseLeave,
    onClick,
}: PieChartInnerProps) {
    if (!data.length) return null;

    const sorter = (a: Datum, b: Datum) => (a.key > b.key ? 1 : -1);
    const sortedData = data.sort(sorter);
    const radius = Math.min(width, height) / 2;
    const centerY = height / 2;
    const centerX = width / 2;
    const pieValue = (d: Datum) => d.value;

    return (
        <svg width={width} height={height}>
            <Group top={centerY} left={centerX}>
                <Pie
                    data={sortedData}
                    pieValue={pieValue}
                    pieSort={sorter}
                    outerRadius={radius}
                    innerRadius={radius / 3}
                >
                    {(pie) =>
                        pie.arcs.map((arc) => (
                            <Slice
                                key={arc.data.key}
                                pie={pie}
                                arc={arc}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onClick={onClick}
                            />
                        ))
                    }
                </Pie>
            </Group>
        </svg>
    );
}

interface SliceProps
    extends Pick<PieChartProps, 'onMouseEnter' | 'onMouseLeave' | 'onClick'> {
    pie: ProvidedProps<Datum>;
    arc: PieArcDatum<Datum>;
}

function Slice({ pie, arc, onMouseEnter, onMouseLeave, onClick }: SliceProps) {
    const startAngle = useMotionValue(arc.startAngle);
    const endAngle = useMotionValue(arc.endAngle);

    const newStartAngle = arc.startAngle;
    const newEndAngle = arc.endAngle;

    useEffect(() => {
        if (
            startAngle.get() !== newStartAngle ||
            endAngle.get() !== newEndAngle
        ) {
            animate(startAngle, newStartAngle);
            animate(endAngle, newEndAngle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newStartAngle, newEndAngle]);

    // This transform effectively implements a traditional d3 arcTween, resulting
    // in smooth tweening between different path values based on arc angle inputs.
    const d = useTransform([startAngle, endAngle], (inputs) => {
        // N.B. Typecast necessary because useTransform doesn't pass through
        // input types correctly.
        const [latestStartAngle, latestEndAngle] = inputs as [number, number];
        return pie.path({
            ...arc,
            startAngle: latestStartAngle,
            endAngle: latestEndAngle,
        });
    });

    if (!d) return null;

    return (
        <MotionPath
            fill={arc.data.color}
            cursor={onClick ? 'pointer' : undefined}
            {...(onClick && {
                _hover: {
                    fill: chroma(arc.data.color).brighten(0.2).hex(),
                },
            })}
            d={d}
            onMouseEnter={() => {
                onMouseEnter?.(arc.data);
            }}
            onMouseLeave={() => {
                onMouseLeave?.();
            }}
            onClick={() => {
                onClick?.(arc.data);
            }}
        />
    );
}
